import { NavLink } from "react-router-dom"
import { cn } from "@/lib/utils"
import {
  LETRAS_DO_4ESC,
  NOME_DO_PARCEIRO,
  PELE_DO_4ESC,
} from "@/components/parceiros/marcaDo4ESC"

/**
 * O atalho para o 4ESC Tech, no rodapé do menu.
 *
 * ── O desenho: um botão com a marca deles ───────────────────────────────────
 * Pedido do PM em 15/09/2026: fundo escuro, letras na cor da marca, do jeito que
 * a 4ESC escreve "4ESC Tech" no site deles, fonte igual ou parecida, sem ícone e
 * com o texto centralizado. O fundo é a mesma pele escura da placa do logo e dos
 * botões da página do parceiro (`PELE_DO_4ESC`), e as letras são as da marca
 * (`LETRAS_DO_4ESC`). As duas moram em `components/parceiros/marcaDo4ESC.ts`,
 * com as cores e as contas.
 *
 * O que diz "isto é de fora, não é uma seção do CreditOS" são quatro sinais:
 *
 *   1. POSIÇÃO: mora fora do `<ul>` de navegação, no rodapé, depois de um traço.
 *   2. PALAVRA: o rótulo "PARCEIRO" em cima, com o menu aberto.
 *   3. FORMA: é o único item do menu com fundo e contorno próprios, e o único
 *      sem ícone. Os itens do CreditOS são ícone e texto soltos.
 *   4. MARCA: as letras são as da 4ESC, não as nossas.
 *
 * ── O tema escuro, que hoje está desligado ──────────────────────────────────
 * A primeira versão deste atalho recusou "o item escuro": fundo escuro salta no
 * tema claro e se perde no tema escuro. O argumento continua certo, e o desenho
 * novo não depende do fundo para destacar no escuro: a letra em degradê é clara
 * e continua saltando sobre qualquer escuro. Por isso ainda não há NENHUMA
 * variante `dark:` aqui, e o teste reprova se alguém acrescentar uma. O atalho é
 * o mesmo desenho nos dois temas.
 *
 * ── O estado ativo ──────────────────────────────────────────────────────────
 * Com a página aberta, os outros itens do menu ganham fundo roxo claro e letra
 * roxa. Aqui isso apagaria a marca, então o "você está aqui" fica dentro da
 * identidade: o fundo passa ao índigo mais escuro da paleta (#1E1B4B) e o
 * contorno acende inteiro (#818CF8). Medido: o contorno aceso contra o apagado dá
 * 3,10 : 1, e contra o fundo ativo, 5,36 : 1. Passar o mouse muda só o fundo
 * (#111827), para não ser confundido com o ativo.
 *
 * ── Menu recolhido ──────────────────────────────────────────────────────────
 * Sem ícone, cabe a forma curta "4ESC", no mesmo degradê. O nome inteiro continua
 * no nome acessível (o leitor de tela lê "4ESC Tech") e na dica do mouse.
 *
 * ── Por que ele leva para a nossa página, e não direto para fora ────────────
 * Um item de menu que joga a pessoa para outro produto sem aviso é alçapão. E é
 * na nossa página que mora a frase que torna este atalho aceitável: nenhum dado
 * do CreditOS é enviado. Se o clique fosse direto para fora, essa frase viraria
 * opcional. Quem leva para fora é o botão de lá, com aviso. Por isso aqui não há
 * ícone de link externo: o clique não sai do CreditOS.
 */

/** A rota da nossa página de apresentação do parceiro. */
export const ROTA_DO_PARCEIRO = "/parceiros/4esc"

/** O que cabe no botão com o menu recolhido (64 px), no lugar do nome inteiro. */
export const NOME_CURTO_DO_PARCEIRO = "4ESC"

/** Com a página do parceiro aberta: o índigo mais escuro e o contorno aceso. */
const ATIVO = "bg-[#1E1B4B] border-[#818CF8]"

/** Mouse em cima, fora da página do parceiro: só o fundo clareia. */
const AO_PASSAR_O_MOUSE = "hover:bg-[#111827]"

interface AtalhoDoParceiroProps {
  /** Menu recolhido (64 px): fica a forma curta, com o nome inteiro no nome acessível e no title. */
  recolhido?: boolean
  /** No celular o menu precisa fechar antes de navegar. */
  onNavegar?: () => void
}

export function AtalhoDoParceiro({ recolhido = false, onNavegar }: AtalhoDoParceiroProps) {
  return (
    <div
      data-testid="rodape-do-parceiro"
      className="mt-auto pt-3 px-2 border-t border-glass-border"
    >
      {!recolhido && (
        <p className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Parceiro
        </p>
      )}

      <NavLink
        to={ROTA_DO_PARCEIRO}
        onClick={onNavegar}
        data-testid="atalho-do-parceiro"
        aria-label={recolhido ? NOME_DO_PARCEIRO : undefined}
        title={recolhido ? `${NOME_DO_PARCEIRO} (parceiro)` : undefined}
        className={({ isActive }) =>
          cn(
            "flex items-center justify-center overflow-hidden whitespace-nowrap rounded-xl py-2 text-center",
            recolhido ? "px-1" : "px-3",
            // O `a:hover` global sublinha todo link; embaixo de uma marca, não.
            "transition-colors hover:no-underline",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)] focus-visible:ring-offset-2",
            PELE_DO_4ESC,
            isActive ? ATIVO : AO_PASSAR_O_MOUSE,
          )
        }
      >
        <span
          className={cn(
            LETRAS_DO_4ESC,
            recolhido ? "text-xs" : "text-base",
            // Depois do tamanho, de propósito: o `cn` apaga a altura de linha que
            // vem antes dele. Mesma linha aberto e recolhido, botão da mesma altura.
            "leading-6",
          )}
        >
          {recolhido ? NOME_CURTO_DO_PARCEIRO : NOME_DO_PARCEIRO}
        </span>
      </NavLink>
    </div>
  )
}
